import { api } from "../api";

/*
 * The cancellation flow's backend: the survey (saved at every step), the retention discount and the
 * cancellation itself.
 */

export type CancelReason =
  | "price"
  | "lowUsage"
  | "missingFeature"
  | "technical"
  | "competitor"
  | "temporary"
  | "businessChange"
  | "onboarding"
  | "other";

export type RetentionOffer = "discount" | "chat" | "benefits";

/** The step the user reached: saved at every step, so leaving mid-way is data too. */
export type CancellationStep = "reason" | "detail" | "value" | "offer" | "confirm" | "canceled" | "retained";

export type CancellationSurvey = {
  /** One per run of the flow (a new one each time the user starts it). */
  surveyId: string;
  siteId: string;
  step: CancellationStep;
  reason?: CancelReason;
  /** Free text for "other". */
  reasonText?: string;
  /** The follow-up question of the chosen reason. */
  detail?: { options?: string[]; text?: string };
  /** "How much did Keepo Plus help your business?", 1 to 5. */
  value?: number;
  valueComment?: string;
  offersShown?: RetentionOffer[];
  offerAccepted?: RetentionOffer;
  /** What the app already knows; the backend adds plan, tenure, visits and last activity. */
  context: {
    benefitsUsed: number;
    benefitsTotal: number;
    unusedBenefits: string[];
    hasCustomDomain: boolean;
    keeposCount: number;
    plusKeeposCount: number;
    locale: string;
  };
};

export const cancellationSurveyService = {
  async saveProgress(survey: CancellationSurvey): Promise<void> {
    await api.saveCancellationSurvey(survey.siteId, survey);
  },

  /** Whether the 20% retention discount can be offered (any subscription trying to cancel, see the backend's rules). */
  async getDiscountEligibility(siteId: string): Promise<boolean> {
    const { eligible } = await api.getRetentionDiscount(siteId);
    return !!eligible;
  },

  /** Applies the 20% discount to the next charge. */
  async applyDiscount(siteId: string): Promise<void> {
    await api.applyRetentionDiscount(siteId);
  },

  /** Cancels the subscription: see `api.cancelSubscription`. */
  async cancelSubscription(siteId: string) {
    return api.cancelSubscription(siteId);
  },
};
